import React from "react";
import {
  Collapse,
  Button,
  Space,
  Input,
  Modal,
  message,
  Progress,
  Radio,
} from "antd";
import {
  FolderAddOutlined,
  FolderOutlined,
  CloudUploadOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import FileGrid from "./FileGrid";
import FileList from "./FileList";
import FilePreview from "./FilePreview";
import {
  listFolder,
  createFolder,
  renameItem,
  deleteItems,
  getDownloadUrl,
  getUploadUrl,
  getUploadHeaders,
  getFolderOrder,
  saveFolderOrder,
  getPrefs,
  savePrefs,
} from "./fileManagerApi";
import showNotification from "src/components/stateless/notifications";

import "./styles.scss";

const PANEL_COLORS = [
  "#1890ff", "#52c41a", "#faad14", "#f5222d",
  "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16",
];

function applyOrder(folders, savedOrder) {
  if (!savedOrder || !savedOrder.length) return folders;
  const folderMap = {};
  folders.forEach((f) => { folderMap[f.path] = f; });
  const ordered = [];
  savedOrder.forEach((p) => {
    if (folderMap[p]) {
      ordered.push(folderMap[p]);
      delete folderMap[p];
    }
  });
  Object.values(folderMap).forEach((f) => ordered.push(f));
  return ordered;
}

class FileManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      folders: [],
      folderFiles: {},    // { folderPath: [files] }
      loadingFolders: {}, // { folderPath: true/false }
      activeKeys: [],
      // Upload state per folder
      uploads: {},        // { folderPath: [{name, progress, status}] }
      draggingFolder: null,
      viewMode: "list", // "list" or "grid"
      fileOrders: {},   // { folderPath: [fileName, ...] }
      // Reorder drag
      dragReorderIdx: null,
      dragOverIdx: null,
      // Modals
      createFolderVisible: false,
      newFolderName: "",
      renameVisible: false,
      renameTarget: null,
      renameName: "",
      previewFile: null,
    };
  }

  componentDidMount() {
    this.loadRootFolders();
    this.loadPrefs();
  }

  loadPrefs = async () => {
    try {
      const prefs = await getPrefs();
      const newState = {};
      if (prefs.viewMode) newState.viewMode = prefs.viewMode;
      if (prefs.fileOrders) newState.fileOrders = prefs.fileOrders;
      if (Object.keys(newState).length) this.setState(newState);
    } catch (e) { /* ignore */ }
  };

  onViewModeChange = (e) => {
    const viewMode = e.target.value;
    this.setState({viewMode});
    savePrefs({viewMode}).catch(() => {});
  };

  loadRootFolders = async () => {
    try {
      const data = await listFolder("/");
      let order = [];
      try { order = await getFolderOrder(); } catch (e) { /* ignore */ }
      const folders = applyOrder(data.folders || [], order);
      this.setState({folders});
    } catch (err) {
      showNotification("error", "Erreur", "Impossible de charger les dossiers");
    }
  };

  applyFileOrder = (folderPath, files) => {
    const {fileOrders} = this.state;
    const order = fileOrders[folderPath];
    if (!order || !order.length) return files;
    const fileMap = {};
    files.forEach((f) => { fileMap[f.name] = f; });
    const ordered = [];
    order.forEach((name) => {
      if (fileMap[name]) {
        ordered.push(fileMap[name]);
        delete fileMap[name];
      }
    });
    // Append new files not in saved order (at the end)
    Object.values(fileMap).forEach((f) => ordered.push(f));
    return ordered;
  };

  loadFolderFiles = async (folderPath) => {
    this.setState((prev) => ({
      loadingFolders: {...prev.loadingFolders, [folderPath]: true},
    }));
    try {
      const data = await listFolder(folderPath);
      const files = this.applyFileOrder(folderPath, data.files || []);
      this.setState((prev) => ({
        folderFiles: {...prev.folderFiles, [folderPath]: files},
        loadingFolders: {...prev.loadingFolders, [folderPath]: false},
      }));
    } catch (err) {
      this.setState((prev) => ({
        loadingFolders: {...prev.loadingFolders, [folderPath]: false},
      }));
    }
  };

  onCollapseChange = (activeKeys) => {
    this.setState({activeKeys});
    // Load files for newly opened panels
    for (const key of activeKeys) {
      if (!this.state.folderFiles[key]) {
        this.loadFolderFiles(key);
      }
    }
  };

  // Create folder
  onCreateFolder = async () => {
    const {newFolderName} = this.state;
    if (!newFolderName.trim()) return;
    const folderPath = "/" + newFolderName.trim();
    try {
      await createFolder(folderPath);
      showNotification("success", "Succès", "Dossier créé");
      this.setState({createFolderVisible: false, newFolderName: ""});
      this.loadRootFolders();
    } catch (err) {
      showNotification("error", "Erreur", "Impossible de créer le dossier");
    }
  };

  // Rename
  onRename = async () => {
    const {renameTarget, renameName, fileOrders} = this.state;
    if (!renameName.trim() || !renameTarget) return;
    try {
      await renameItem(renameTarget.path, renameName.trim());
      showNotification("success", "Succès", "Élément renommé");
      this.setState({renameVisible: false, renameTarget: null, renameName: ""});
      if (renameTarget.isFolder) {
        this.loadRootFolders();
      } else {
        // Update file order: replace old name with new name at same position
        const parentPath = renameTarget.parentFolder || renameTarget.path.substring(0, renameTarget.path.lastIndexOf("/")) || "/";
        const order = fileOrders[parentPath];
        if (order) {
          const newOrder = order.map((n) => n === renameTarget.name ? renameName.trim() : n);
          const newFileOrders = {...fileOrders, [parentPath]: newOrder};
          this.setState({fileOrders: newFileOrders});
          savePrefs({fileOrders: newFileOrders}).catch(() => {});
        }
        this.loadFolderFiles(parentPath);
      }
    } catch (err) {
      showNotification("error", "Erreur", "Impossible de renommer");
    }
  };

  // Delete
  onDelete = async (paths, folderPath) => {
    try {
      await deleteItems(paths);
      showNotification("success", "Succès", `${paths.length} élément(s) supprimé(s)`);
      if (folderPath) {
        this.loadFolderFiles(folderPath);
      } else {
        this.loadRootFolders();
      }
    } catch (err) {
      showNotification("error", "Erreur", "Impossible de supprimer");
    }
  };

  // Download
  onDownload = (record) => {
    const url = getDownloadUrl(record.path);
    const token = localStorage.getItem("adv_stats");
    fetch(url, {headers: {Authorization: `Bearer ${token}`}})
      .then((res) => {
        if (!res.ok) throw new Error("Erreur");
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = record.name;
        a.click();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => showNotification("error", "Erreur", "Impossible de télécharger"));
  };

  // Reorder files within a folder
  onFileReorder = (folderPath, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    this.setState((prev) => {
      const files = [...(prev.folderFiles[folderPath] || [])];
      const [moved] = files.splice(fromIndex, 1);
      files.splice(toIndex, 0, moved);

      const fileOrders = {
        ...prev.fileOrders,
        [folderPath]: files.map((f) => f.name),
      };

      // Persist
      savePrefs({fileOrders}).catch(() => {});

      return {
        folderFiles: {...prev.folderFiles, [folderPath]: files},
        fileOrders,
      };
    });
  };

  // Preview
  onPreview = (record) => {
    this.setState({previewFile: record});
  };

  // Drag & drop upload (only for external files, not internal reorder)
  onDragOver = (e, folderPath) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show drop overlay for external file drops, not internal reorder
    if (e.dataTransfer.types.includes("Files") && !e.dataTransfer.types.includes("text/file-idx")) {
      this.setState({draggingFolder: folderPath});
    }
  };

  onDragLeave = (e, folderPath) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.draggingFolder === folderPath) {
      this.setState({draggingFolder: null});
    }
  };

  onDrop = (e, folderPath) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({draggingFolder: null});

    // Ignore internal reorder drops
    if (e.dataTransfer.types.includes("text/file-idx")) return;

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    // Check for files without extension
    for (let i = 0; i < droppedFiles.length; i++) {
      const name = droppedFiles[i].name;
      if (!name.includes(".") || name.endsWith(".")) {
        message.warning(`Fichier sans extension : ${name}`);
      }
    }

    // Upload each file with progress via XHR
    for (let i = 0; i < droppedFiles.length; i++) {
      this.uploadFile(droppedFiles[i], folderPath);
    }
  };

  uploadFile = (file, folderPath) => {
    const uploadId = `${folderPath}-${file.name}-${Date.now()}`;
    const uploadEntry = {id: uploadId, name: file.name, progress: 0, status: "uploading"};

    this.setState((prev) => ({
      uploads: {
        ...prev.uploads,
        [folderPath]: [...(prev.uploads[folderPath] || []), uploadEntry],
      },
    }));

    const formData = new FormData();
    formData.append("files[]", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", getUploadUrl(folderPath));

    const headers = getUploadHeaders();
    Object.keys(headers).forEach((key) => xhr.setRequestHeader(key, headers[key]));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 100);
        this.setState((prev) => ({
          uploads: {
            ...prev.uploads,
            [folderPath]: (prev.uploads[folderPath] || []).map((u) =>
              u.id === uploadId ? {...u, progress} : u
            ),
          },
        }));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        this.setState((prev) => ({
          uploads: {
            ...prev.uploads,
            [folderPath]: (prev.uploads[folderPath] || []).map((u) =>
              u.id === uploadId ? {...u, progress: 100, status: "done"} : u
            ),
          },
        }));
        message.success(`${file.name} uploadé`);
        this.loadFolderFiles(folderPath);
        // Clear completed upload after 3s
        setTimeout(() => {
          this.setState((prev) => ({
            uploads: {
              ...prev.uploads,
              [folderPath]: (prev.uploads[folderPath] || []).filter((u) => u.id !== uploadId),
            },
          }));
        }, 3000);
      } else {
        this.setState((prev) => ({
          uploads: {
            ...prev.uploads,
            [folderPath]: (prev.uploads[folderPath] || []).map((u) =>
              u.id === uploadId ? {...u, status: "error"} : u
            ),
          },
        }));
        message.error(`Erreur d'upload : ${file.name}`);
      }
    };

    xhr.onerror = () => {
      this.setState((prev) => ({
        uploads: {
          ...prev.uploads,
          [folderPath]: (prev.uploads[folderPath] || []).map((u) =>
            u.id === uploadId ? {...u, status: "error"} : u
          ),
        },
      }));
      message.error(`Erreur d'upload : ${file.name}`);
    };

    xhr.send(formData);
  };

  // Reorder panels
  onPanelDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/panel-index", String(index));
    this.setState({dragReorderIdx: index});
  };

  onCollapseDragOver = (e) => {
    if (this.state.dragReorderIdx === null) return;
    e.preventDefault();
    // Find which panel we're over based on Y position
    const collapseEl = this.collapseRef;
    if (!collapseEl) return;
    const panels = collapseEl.querySelectorAll(".ant-collapse-item");
    let targetIdx = null;
    panels.forEach((panel, idx) => {
      const rect = panel.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        targetIdx = idx;
      }
    });
    if (targetIdx !== null && targetIdx !== this.state.dragOverIdx) {
      this.setState({dragOverIdx: targetIdx});
    }
  };

  onPanelDragEnd = () => {
    if (this.state.dragReorderIdx === null) return;
    const {dragReorderIdx, dragOverIdx} = this.state;
    if (dragOverIdx !== null && dragReorderIdx !== dragOverIdx) {
      this.setState((prev) => {
        const newFolders = [...prev.folders];
        const [moved] = newFolders.splice(dragReorderIdx, 1);
        newFolders.splice(dragOverIdx, 0, moved);
        saveFolderOrder(newFolders.map((f) => f.path));
        return {folders: newFolders, dragReorderIdx: null, dragOverIdx: null};
      });
    } else {
      this.setState({dragReorderIdx: null, dragOverIdx: null});
    }
  };

  // Delete folder (admin)
  onDeleteFolder = (folder) => {
    Modal.confirm({
      title: "Confirmer la suppression",
      content: `Supprimer le dossier "${folder.name}" et tout son contenu ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => this.onDelete([folder.path], null),
    });
  };

  renderPanelHeader = (folder, index) => {
    const {user} = this.props;
    const isAdmin = user && user.role && user.role.name === "Admin";

    return (
      <div
        draggable={isAdmin}
        onDragStart={isAdmin ? (e) => { e.stopPropagation(); this.onPanelDragStart(e, index); } : undefined}
        onDragEnd={isAdmin ? this.onPanelDragEnd : undefined}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
          cursor: isAdmin ? "grab" : "default",
        }}
      >
        <span style={{display: "flex", alignItems: "center"}}>
          <FolderOutlined style={{marginRight: 8, color: PANEL_COLORS[index % PANEL_COLORS.length]}} />
          {folder.name}
        </span>
        {isAdmin && (
          <Space size="small" onClick={(e) => e.stopPropagation()}>
            <EditOutlined
              style={{color: "#595959"}}
              onClick={(e) => {
                e.stopPropagation();
                this.setState({renameVisible: true, renameTarget: {...folder, isFolder: true}, renameName: folder.name});
              }}
            />
            <DeleteOutlined
              style={{color: "#ff4d4f"}}
              onClick={(e) => {
                e.stopPropagation();
                this.onDeleteFolder(folder);
              }}
            />
          </Space>
        )}
      </div>
    );
  };

  render() {
    const {user} = this.props;
    const isAdmin = user && user.role && user.role.name === "Admin";
    const {
      folders, folderFiles, loadingFolders, activeKeys, uploads,
      draggingFolder, createFolderVisible, newFolderName,
      renameVisible, renameName, previewFile,
    } = this.state;

    return (
      <div className="file-manager">
        {/* Toolbar */}
        <div className="file-manager-toolbar">
          <Space>
            {isAdmin && (
              <Button
                icon={<FolderAddOutlined />}
                onClick={() => this.setState({createFolderVisible: true})}
              >
                Nouveau dossier
              </Button>
            )}
            <Radio.Group value={this.state.viewMode} onChange={this.onViewModeChange} size="small">
              <Radio.Button value="list"><UnorderedListOutlined /> Liste</Radio.Button>
              <Radio.Button value="grid"><AppstoreOutlined /> Icônes</Radio.Button>
            </Radio.Group>
          </Space>
        </div>

        {/* Accordions */}
        <div ref={(el) => { this.collapseRef = el; }} onDragOver={this.onCollapseDragOver}>
        <Collapse
          activeKey={activeKeys}
          onChange={this.onCollapseChange}
          expandIconPosition="left"
        >
          {folders.map((folder, index) => {
            const panelClass = [
              this.state.dragReorderIdx === index ? "dragging" : "",
              this.state.dragOverIdx === index && this.state.dragReorderIdx !== index ? "drag-over" : "",
            ].filter(Boolean).join(" ");
            return (
            <Collapse.Panel
              key={folder.path}
              header={this.renderPanelHeader(folder, index)}
              className={panelClass}
              style={{borderLeft: `4px solid ${PANEL_COLORS[index % PANEL_COLORS.length]}`}}
            >
              <div
                className="folder-drop-zone"
                onDragOver={isAdmin ? (e) => this.onDragOver(e, folder.path) : undefined}
                onDragLeave={isAdmin ? (e) => this.onDragLeave(e, folder.path) : undefined}
                onDrop={isAdmin ? (e) => this.onDrop(e, folder.path) : undefined}
              >
                {isAdmin && draggingFolder === folder.path && (
                  <div className="folder-drop-overlay">
                    <CloudUploadOutlined style={{fontSize: 24, marginRight: 8}} />
                    Déposez vos fichiers ici
                  </div>
                )}

                {loadingFolders[folder.path] ? (
                  <div style={{textAlign: "center", padding: 24, color: "#bfbfbf"}}>Chargement...</div>
                ) : this.state.viewMode === "grid" ? (
                  <FileGrid
                    files={folderFiles[folder.path] || []}
                    onDownload={this.onDownload}
                    onPreview={this.onPreview}
                    onRename={isAdmin ? (file) =>
                      this.setState({renameVisible: true, renameTarget: {...file, parentFolder: folder.path}, renameName: file.name})
                    : undefined}
                    onDelete={isAdmin ? (paths) => this.onDelete(paths, folder.path) : undefined}
                    isAdmin={isAdmin}
                    onReorder={isAdmin ? (from, to) => this.onFileReorder(folder.path, from, to) : undefined}
                  />
                ) : (
                  <FileList
                    files={folderFiles[folder.path] || []}
                    onDownload={this.onDownload}
                    onPreview={this.onPreview}
                    onRename={isAdmin ? (file) =>
                      this.setState({renameVisible: true, renameTarget: {...file, parentFolder: folder.path}, renameName: file.name})
                    : undefined}
                    onDelete={isAdmin ? (paths) => this.onDelete(paths, folder.path) : undefined}
                    isAdmin={isAdmin}
                    onReorder={isAdmin ? (from, to) => this.onFileReorder(folder.path, from, to) : undefined}
                  />
                )}

                {/* Upload progress */}
                {uploads[folder.path] && uploads[folder.path].length > 0 && (
                  <div className="upload-progress-list">
                    {uploads[folder.path].map((u) => (
                      <div key={u.id} className="upload-progress-item">
                        <span style={{minWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                          {u.name}
                        </span>
                        <Progress
                          percent={u.progress}
                          size="small"
                          status={u.status === "error" ? "exception" : u.status === "done" ? "success" : "active"}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Collapse.Panel>
            );
          })}
        </Collapse>
        </div>

        {/* Preview */}
        {previewFile && (
          <FilePreview
            file={previewFile}
            onClose={() => this.setState({previewFile: null})}
          />
        )}

        {/* Create Folder Modal */}
        <Modal
          title="Nouveau dossier"
          visible={createFolderVisible}
          onOk={this.onCreateFolder}
          onCancel={() => this.setState({createFolderVisible: false, newFolderName: ""})}
          okText="Créer"
          cancelText="Annuler"
        >
          <Input
            placeholder="Nom du dossier"
            value={newFolderName}
            onChange={(e) => this.setState({newFolderName: e.target.value})}
            onPressEnter={this.onCreateFolder}
            autoFocus
          />
        </Modal>

        {/* Rename Modal */}
        <Modal
          title="Renommer"
          visible={renameVisible}
          onOk={this.onRename}
          onCancel={() => this.setState({renameVisible: false, renameTarget: null, renameName: ""})}
          okText="Renommer"
          cancelText="Annuler"
        >
          <Input
            placeholder="Nouveau nom"
            value={renameName}
            onChange={(e) => this.setState({renameName: e.target.value})}
            onPressEnter={this.onRename}
            autoFocus
          />
        </Modal>
      </div>
    );
  }
}

export default FileManager;
